'use client';

import { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';

interface FeaturedBannerProps {
  headline: string;
  subtitle: string;
  ctaText: string;
  ctaLink: string;
  backgroundImage?: string;
  backgroundVideo?: string;
  imageAlt?: string;
}

export default function FeaturedBanner({
  headline,
  subtitle,
  ctaText,
  ctaLink,
  backgroundImage,
  backgroundVideo,
  imageAlt = 'Featured banner background',
}: FeaturedBannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay might be blocked, that's okay
      });
    }
  }, []);

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  // Default placeholder image from Shopify CDN
  const defaultImage = 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-lifestyle-1_large.png';
  const imageSrc = backgroundImage || defaultImage;

  return (
    <section className="featured-banner relative w-full h-[70vh] lg:h-[80vh] overflow-hidden">
      {/* Background Video */}
      {backgroundVideo && (
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            isVideoLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          src={backgroundVideo}
          muted
          loop
          playsInline
          autoPlay
          onLoadedData={handleVideoLoad}
        />
      )}

      {/* Background Image (fallback or primary) */}
      <div
        className={`absolute inset-0 ${
          backgroundVideo && isVideoLoaded ? 'opacity-0' : 'opacity-100'
        } transition-opacity duration-500`}
      >
        <Image
          src={imageSrc}
          alt={imageAlt}
          fill
          className="object-cover"
          priority
          sizes="100vw"
        />
      </div>

      {/* Gradient Overlay - stronger on bottom-left for text readability */}
      <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/20 to-transparent" />

      {/* Content - Bottom Left */}
      <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-12 xl:p-16">
        <div className="container mx-auto">
          <div>
            {/* Headline */}
            <h2
              className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold uppercase tracking-tight mb-3 lg:mb-4"
              style={{ color: '#ffffff' }}
            >
              {headline}
            </h2>

            {/* Subtitle */}
            <p style={{ color: 'rgba(255, 255, 255, 0.9)' }} className="text-sm md:text-base lg:text-lg mb-4 lg:mb-6">
              {subtitle}
            </p>

            {/* CTA Link */}
            <Link
              href={ctaLink}
              className="inline-block text-sm md:text-base font-medium border-b-2 border-white pb-1 hover:border-white/80 transition-colors whitespace-nowrap"
              style={{ color: '#ffffff' }}
            >
              {ctaText}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
